import { EFinancingProgram, EWorksheetDecision } from '@if/core/types'
import { Grid, Stack } from '@mui/material'
import { Breadcrumb, InputTextField, PageError, PageSpinner } from '@src/components'
import PendingMessage from '@src/components/PendingMessage'
import { MechanicalWarrantyComputeRequestDto } from '@src/containers/EditWorksheet/EditMechanicalWarrantyWorksheetPage/types/ComputeMechanicalWarrantyRequestDto'
import { MechanicalWarrantyComputeResultDto } from '@src/containers/EditWorksheet/EditMechanicalWarrantyWorksheetPage/types/ComputeMechanicalWarrantyResultDto'
import {
  MechanicalWarrantyDecisionDTO,
  MechanicalWarrantyWorksheet,
} from '@src/containers/EditWorksheet/EditMechanicalWarrantyWorksheetPage/types/mechanicalWarrantyWorksheetSchema'
import PrivateMessage from '@src/containers/ViewCreditApplicationPage/components/privateMessage'
import WarningMessageAccordion from '@src/containers/ViewCreditApplicationPage/components/warningMessageAccordion'
import { useCreditApplicationContext, useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useGetAvailableCredit } from '@src/data/api/available-credit-api'
import { useInvalidateFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import {
  useComputeMechanicalWarrantyLoan,
  useUpdateMechanicalWarrantyWorksheetDecision,
} from '@src/data/api/mechanical-warranty-worksheet/mechanical-warranty-worksheet-api'
import {
  useGetMerchantById,
  useGetMerchantProviderByType,
  useGetMerchantProviderProducts,
} from '@src/data/api/merchant/merchant-api'
import { useDeleteMessage } from '@src/data/api/message/message-api'
import { useBusinessErrors } from '@src/data/selectors/app-config-selectors'
import {
  useAreAllTasksRequiredForCVTCompleted,
  useConfirmAllIncomesStatus,
  useCurrentCreditApplicationSpecificNorms,
  useIsFinalDecisionPending,
} from '@src/data/selectors/credit-selectors'
import { useAreAllDocumentsRequiredForCVT } from '@src/data/selectors/document-selectors'
import {
  BaseWorksheetFundingThresholdExtend,
  CreditApplicationMessage,
  NormsMessage,
  WorksheetDecision,
} from '@src/data/types'
import { useUser } from '@src/providers/UserProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import translate from '@src/services/translate'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MechanicalWarrantySummaryBox from '../components/LoanSummary/MechanicalWarrantySummaryBox'
import WorksheetDecisionDialog from '../components/LoanSummary/WorksheetDecisionDialog'
import WorksheetDecisionSection from '../components/LoanSummary/WorksheetDecisionSection'
import BorrowerSection from '../components/Transaction/BorrowerSection'
import FinancingSection from '../components/Transaction/FinancingSection'
import MechanicalWarrantySection from '../components/Transaction/MechanicalWarrantySection'
import SectionContainer from '../components/Transaction/SectionContainer'
import { useDialogDecisionDefaultValue, useViewTotalObligation } from '../viewWorksheet-hooks'
import { getEmptyMechanicalWarrantyComputeResponse } from '../viewWorksheet-selectors'

const MechanicalWarrantyWorksheetViewPage = () => {
  const { t } = useTranslation()

  const { creditApplication, worksheet, isLoading, privateMessages } = useCreditApplicationData()
  const mechanicalWorksheet = worksheet as MechanicalWarrantyWorksheet | undefined

  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()
  const [computeFunding] = useComputeMechanicalWarrantyLoan()
  const [updateDecision] = useUpdateMechanicalWarrantyWorksheetDecision()
  const [deleteMessage] = useDeleteMessage()
  const EditDecisionDialogOpen = 'EditDecisionDialogOpen'
  const [currentOpenDialog, setCurrentOpenDialog] = useState<string>('')

  const [computedInfo, setComputedInfo] = useState<MechanicalWarrantyComputeResultDto>(
    {} as MechanicalWarrantyComputeResultDto,
  )

  const finalDecisionIsPending = useIsFinalDecisionPending()
  const error = useBusinessErrors()

  const { id, financingProgramId } = useCreditApplicationContext()
  const [availableCreditData] = useGetAvailableCredit(financingProgramId, id)
  const availableCredit = availableCreditData?.availableAmount ?? 0
  const creditInProgress = availableCreditData?.creditInProgress ?? 0
  const commonNormsMessage: NormsMessage[] = useCurrentCreditApplicationSpecificNorms()
  const user = useUser()

  const worksheetNormWarningMessages = useMemo(() => {
    if (!mechanicalWorksheet?.normsDecision?.warningMessages) return []
    return mechanicalWorksheet.normsDecision.warningMessages.map((wr) => {
      const translatedMessage = translate([`worksheet.norms.${wr.id}`])
      let messageSuffix = ' | '
      wr.parameters.forEach((p, i) => {
        if (i >= 1) messageSuffix = messageSuffix.concat(', ')
        const suffix = translate(`worksheet.paramNames.${p.name}`, { val: p.value })
        messageSuffix = messageSuffix.concat(suffix)
      })
      return {
        id: wr.id,
        message: translatedMessage.concat(messageSuffix),
        applicantType: null,
      } as NormsMessage
    })
  }, [mechanicalWorksheet?.normsDecision?.warningMessages])
  const { documents, requiredDocuments } = useCreditApplicationData()
  const areAllTasksRequiredForCVTCompleted = useAreAllTasksRequiredForCVTCompleted()
  const areAllDocumentsRequiredForCVTApproved = useAreAllDocumentsRequiredForCVT(documents, requiredDocuments)
  const confirmAllIncomesStatus = useConfirmAllIncomesStatus()
  const totalObligation = useViewTotalObligation(mechanicalWorksheet?.amountRequested)
  const [merchant] = useGetMerchantById(creditApplication?.merchantId ?? '')
  const activePaymentPlan = merchant?.paymentPlans?.[0] ?? null

  const [mechanicalWarrantyProviders] = useGetMerchantProviderByType(
    creditApplication?.merchantId ?? '',
    EFinancingProgram.MechanicalWarranty as unknown as number,
  )

  const [mechanicalProducts] = useGetMerchantProviderProducts(
    creditApplication?.merchantId ?? '',
    EFinancingProgram.MechanicalWarranty as unknown as number,
    mechanicalWorksheet?.providerId ?? '',
  )

  const selectedProvider = mechanicalWarrantyProviders.find(
    (provider) => provider.id === mechanicalWorksheet?.providerId,
  )

  const selectedMechanicalProduct = mechanicalProducts.find(
    (product) => product.id === mechanicalWorksheet?.mechanicalWarrantyProductId,
  )

  const editDisabled = creditApplication?.editLocked === true || finalDecisionIsPending
  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.MechanicalWarranty}/${creditApplication?.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  const dialogDecisionDefaultValue = useDialogDecisionDefaultValue(
    creditApplication!,
    mechanicalWorksheet!,
    areAllTasksRequiredForCVTCompleted,
    areAllDocumentsRequiredForCVTApproved,
    confirmAllIncomesStatus!,
  )

  const handleDeletingPrivateMessage = async (message: CreditApplicationMessage) => {
    if (creditApplication?.id) {
      await deleteMessage({
        id: message.id,
        creditApplicationId: message.creditApplicationId,
        financingProgramId: creditApplication.financingProgramId,
      })
    }
  }

  useEffect(() => {
    if (creditApplication && mechanicalWorksheet && Object.keys(computedInfo).length === 0) {
      const computeDto: MechanicalWarrantyComputeRequestDto = {
        paymentFrequency: mechanicalWorksheet.paymentFrequency,
        term: mechanicalWorksheet.term,
        firstPaymentDate: mechanicalWorksheet.firstPaymentOn,
        deliveryOn: mechanicalWorksheet.deliveryOn,
        options: mechanicalWorksheet.additions.options,
        surcharges: mechanicalWorksheet.additions.surcharges,
        creditApplicationId: creditApplication.id,
        providerId: mechanicalWorksheet.providerId,
        mechanicalWarrantyProductId: mechanicalWorksheet.mechanicalWarrantyProductId,
      }

      if (computeDto) {
        const fetchFunding = async () => {
          try {
            const data = await computeFunding(computeDto)
            setComputedInfo(data)
          } catch (error) {
            reportErrorToConsole(error)
          }
        }
        void fetchFunding()
      } else {
        const emptyComputedDto = getEmptyMechanicalWarrantyComputeResponse()
        setComputedInfo(emptyComputedDto)
      }
    }
  }, [mechanicalWorksheet, computedInfo, creditApplication, computeFunding])

  // callbacks
  const closeDialogs = useCallback(() => {
    setCurrentOpenDialog('')
  }, [])

  const handleSaveDecision = useCallback(
    async (data: WorksheetDecision) => {
      if (mechanicalWorksheet) {
        const updatedFinalDecision: MechanicalWarrantyDecisionDTO = {
          creditApplicationId: mechanicalWorksheet.creditApplicationId,
          decision: data.decision,
          otherReason: data.otherReason,
          versionTag: mechanicalWorksheet.versionTag,
        } as MechanicalWarrantyDecisionDTO

        if (data.decision === EWorksheetDecision.ApprovedWithCondition) {
          const fundingThresholdExtend: BaseWorksheetFundingThresholdExtend = {
            maxPmtAmount: data.maxPmtAmount,
            maxTotalAmountFinanced: data.maxTotalAmountFinanced,
            maxTermDuration: data.maxTermDuration,
          }
          updatedFinalDecision.fundingThresholdExtend = fundingThresholdExtend
        }

        try {
          await updateDecision({
            ...updatedFinalDecision,
            creditApplicationId: creditApplication?.id,
          })
          closeDialogs()
          await invalidateFullCreditApplication(creditApplication!.id, creditApplication!.financingProgramId)
        } catch (error) {
          reportErrorToConsole(error)
        }
      }
    },
    [mechanicalWorksheet, creditApplication, closeDialogs, invalidateFullCreditApplication, updateDecision],
  )

  return (
    <div>
      <PageSpinner isLoading={isLoading} />
      {!isLoading && mechanicalWorksheet && creditApplication && (
        <>
          <Breadcrumb trees={breadCrumbs} />
          <Stack>
            <PageError errors={error} />
            {finalDecisionIsPending && <PendingMessage />}
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <InputTextField value="GM" label={t('worksheetCommon.program')} disabled />
              <InputTextField
                label={t('worksheetCommon.creditLimit')}
                disabled
                value={creditApplication?.finalCreditDecision?.maxAmountFinanced ?? 0}
              />
              <InputTextField label={t('worksheetCommon.creditInProgress')} disabled value={creditInProgress} />
              <InputTextField label={t('worksheetCommon.availableCredit')} disabled value={availableCredit} />
            </Stack>
            {commonNormsMessage?.length > 0 && (
              <WarningMessageAccordion
                applicantCreditWarnings={null}
                coapplicantCreditWarnings={null}
                commonCreditWarnings={commonNormsMessage}
                worksheetWarnings={null}
                expanded={false}
              />
            )}
            <PrivateMessage
              messages={privateMessages}
              handleDeletingPrivateMessage={handleDeletingPrivateMessage}
              currentUser={user}
              creditAppCreationDate={creditApplication.createdOn}
            />
            <Grid container textAlign="center" justifyContent="space-around" p={4}>
              <Grid
                border="1px solid"
                borderRadius={2}
                p={4}
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <MechanicalWarrantySummaryBox
                  worksheet={mechanicalWorksheet}
                  computedInfo={computedInfo}
                  interestsRate={mechanicalWorksheet?.interestRateOverride ?? creditApplication.finalCreditDecision.interestRate}
                />
              </Grid>
              <Grid
                border="1px solid"
                borderRadius={2}
                p={4}
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <WorksheetDecisionSection
                  worksheet={mechanicalWorksheet}
                  onClick={() => setCurrentOpenDialog(EditDecisionDialogOpen)}
                  editDisabled={editDisabled}
                  userCanEditWorksheet={user?.rights.canEditWorksheet === true}
                />
              </Grid>
            </Grid>
            {selectedProvider && selectedMechanicalProduct && (
              <SectionContainer>
                <FinancingSection
                  worksheet={mechanicalWorksheet}
                  creditApplication={creditApplication}
                  totalObligation={totalObligation}
                  merchantPaymentPlan={activePaymentPlan ?? null}
                  interestRateOverride={mechanicalWorksheet?.interestRateOverride ?? undefined}
                />
                <BorrowerSection worksheet={mechanicalWorksheet} />
                <MechanicalWarrantySection
                  mechanicalWarrantyProvider={selectedProvider}
                  mechanicalProduct={selectedMechanicalProduct}
                />
              </SectionContainer>
            )}
          </Stack>
          <WorksheetDecisionDialog
            open={currentOpenDialog === EditDecisionDialogOpen}
            title={t('worksheet.decision')}
            onCancel={closeDialogs}
            onConfirm={handleSaveDecision}
            worksheetWarnings={worksheetNormWarningMessages}
            defaultValue={dialogDecisionDefaultValue}
            worksheet={mechanicalWorksheet}
            creditapplication={creditApplication}
          />
        </>
      )}
    </div>
  )
}

export default MechanicalWarrantyWorksheetViewPage
